import { useQuery } from "@tanstack/react-query";
import type {
  CustomerTicketDetail,
  CustomerTicketRow,
  InboxFilter,
  InboxResponse,
  SupportAgentRow,
  SupportNotificationSettingsDto,
  SupportNotificationsResponse,
  TicketDetail,
} from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";

export const inboxKey = ["support", "inbox"] as const;
export const ticketKey = (id: string) => ["support", "ticket", id] as const;
export const agentsKey = ["support", "agents"] as const;
export const notificationsKey = ["support", "notifications"] as const;
export const notifySettingsKey = ["support", "notify-settings"] as const;
export const myTicketsKey = ["help", "tickets"] as const;
export const myTicketKey = (id: string) => ["help", "ticket", id] as const;

export const useInbox = (filter: InboxFilter, q: string) =>
  useQuery({
    queryKey: [...inboxKey, filter, q],
    queryFn: () => api<InboxResponse>(`/support/tickets?filter=${filter}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
    placeholderData: (prev) => prev,
  });

export const useTicket = (id: string | undefined) =>
  useQuery({
    queryKey: ticketKey(id ?? ""),
    queryFn: () => api<TicketDetail>(`/support/tickets/${id}`),
    enabled: Boolean(id),
  });

export const useAgents = () =>
  useQuery({ queryKey: agentsKey, queryFn: () => api<SupportAgentRow[]>("/support/agents"), staleTime: 60_000 });

/** Meldingen in de app; ververst bij elk support-event en anders elke minuut (o.a. voor de periodieke taken). */
export const useSupportNotifications = (enabled = true) =>
  useQuery({
    queryKey: notificationsKey,
    queryFn: () => api<SupportNotificationsResponse>("/support/notifications"),
    refetchInterval: 60_000,
    enabled,
  });

export const useNotifySettings = () =>
  useQuery({
    queryKey: notifySettingsKey,
    queryFn: () => api<SupportNotificationSettingsDto>("/support/me/notifications"),
  });

export const useMyTickets = () =>
  useQuery({ queryKey: myTicketsKey, queryFn: () => api<CustomerTicketRow[]>("/help/tickets") });

export const useMyTicket = (id: string | undefined) =>
  useQuery({
    queryKey: myTicketKey(id ?? ""),
    queryFn: () => api<CustomerTicketDetail>(`/help/tickets/${id}`),
    enabled: Boolean(id),
  });

/** Formulier met bijlage (multipart); cookies gaan mee (zelfde origin). */
export async function postForm<T>(path: string, form: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method: "POST", body: form, credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "offline", "Geen verbinding. Versturen kan alleen als je online bent.");
  }
  const data = (await res.json().catch(() => ({}))) as {
    error?: string;
    message?: string;
    issues?: { message: string }[];
  };
  if (!res.ok)
    throw new ApiError(
      res.status,
      data.error ?? "error",
      data.issues?.[0]?.message ?? data.message ?? "Er ging iets mis",
    );
  return data as T;
}

export const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export const fmtSize = (b: number) =>
  b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} kB` : `${(b / 1024 / 1024).toFixed(1)} MB`;
